import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, MenuItem, Button } from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';

import { useGetOrgStructureAssignments } from '../../hooks/orgStructureAssignments/useGetOrgStructureAssignments';
import { useDeleteOrgStructureAssignment } from '../../hooks/orgStructureAssignments/useDeleteOrgStructureAssignment';
import { useDeleteAllOrgStructureAssignments } from '../../hooks/orgStructureAssignments/useDeleteAllOrgStructureAssignments';
import { useDeactivateOrgStructureAssignment } from '../../hooks/orgStructureAssignments/useDeactivateOrgStructureAssignment';
import { useGetSchools } from '../../hooks/schools/useGetSchools';
import { useCurrentContext } from '../../hooks/useCurrentContext';
import { usePermissions } from '../../auth/PermissionsContext';

import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import OrgStructureAssignmentsTableColumns from './Table/OrgStructureAssignmentsTableColumns';
import OrgStructureAssignmentsTableRow from './Table/OrgStructureAssignmentsTableRow';

const OrgStructureAssignmentsTable = () => {
    const { i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const { currentYearId } = useCurrentContext();
    const [filterSchoolId, setFilterSchoolId] = useState('');
    const [filterActive, setFilterActive] = useState('');

    const perms = useMemo(() => ({
        list: has('org_structure_assignments_list'),
        create: has('org_structure_assignments_create'),
        edit: has('org_structure_assignments_edit'),
        delete: has('org_structure_assignments_delete'),
    }), [has]);

    const { deleteAssignment, isDelete } = useDeleteOrgStructureAssignment();
    const { deleteAllAssignments, isDelete: isDeletingAll } = useDeleteAllOrgStructureAssignments();
    const { deactivateAssignment } = useDeactivateOrgStructureAssignment();
    const { schools } = useGetSchools(1, 999);

    const {
        page, perPage, search, sortBy, sortDir, selectedIds, menuAnchorEl, selectedItem,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handleEdit, handleMenuDelete,
        handleBulkDelete, handlePageChange, handlePerPageChange,
    } = useTableActions({
        basePath: '/org-structure-assignments',
        deleteSingle: deleteAssignment,
        deleteAll: deleteAllAssignments,
    });

    const handleDeactivate = () => {
        if (selectedItem) { deactivateAssignment(selectedItem.id); }
        handleMenuClose();
    };

    const filters = useMemo(() => ({
        search: search || undefined,
        sort_by: sortBy || undefined,
        sort_dir: sortDir || undefined,
        academic_year_id: currentYearId || undefined,
        school_id: filterSchoolId || undefined,
        is_active: filterActive !== '' ? filterActive : undefined,
    }), [search, sortBy, sortDir, currentYearId, filterSchoolId, filterActive]);

    const { isLoading, error, assignments, meta } = useGetOrgStructureAssignments(page, perPage, filters);

    const renderRow = (params) => (
        <OrgStructureAssignmentsTableRow
            key={params.key || params.item.id} item={params.item} index={params.index}
            isArabic={params.isArabic} isDark={params.isDark} page={params.page} perPage={params.perPage}
            order={params.order} totalItems={assignments.length} selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick} onMenuOpen={params.onMenuOpen}
        />
    );

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 1, gap: 2 }}>
                <TextField select size="small" value={filterSchoolId}
                    onChange={(e) => setFilterSchoolId(e.target.value)}
                    label={isArabic ? 'فلتر حسب المدرسة' : 'Filter by School'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 200, ...(isArabic && {
                        '& .MuiInputLabel-root': { left: 'unset', right: 32, transformOrigin: 'top right' },
                        '& .MuiInputLabel-shrink': { right: 32 },
                        '& .MuiSelect-icon': { left: 7, right: 'unset' },
                        '& .MuiSelect-select': { textAlign: 'right', paddingRight: '14px !important', paddingLeft: '32px !important' },
                        '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
                    }) }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr' } } } }}>
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    {(schools || []).map((s) => (<MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>))}
                </TextField>

                <TextField select size="small" value={filterActive}
                    onChange={(e) => setFilterActive(e.target.value)}
                    label={isArabic ? 'الحالة' : 'Status'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 150, ...(isArabic && {
                        '& .MuiInputLabel-root': { left: 'unset', right: 32, transformOrigin: 'top right' },
                        '& .MuiInputLabel-shrink': { right: 32 },
                        '& .MuiSelect-icon': { left: 7, right: 'unset' },
                        '& .MuiSelect-select': { textAlign: 'right', paddingRight: '14px !important', paddingLeft: '32px !important' },
                        '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
                    }) }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr' } } } }}>
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    <MenuItem value="1">{isArabic ? 'فعّال' : 'Active'}</MenuItem>
                    <MenuItem value="0">{isArabic ? 'منتهي' : 'Ended'}</MenuItem>
                </TextField>

                {perms.create && (
                    <Button variant="outlined" color="primary" startIcon={<GroupAddIcon />}
                        onClick={() => navigate('/org-structure-assignments/bulk')}
                        sx={{ whiteSpace: 'nowrap', borderRadius: 2.5, fontWeight: 500 }}>
                        {isArabic ? 'تعيين جماعي' : 'Bulk Assign'}
                    </Button>
                )}
            </Box>

            <TableLayout
                title={isArabic ? 'تعيينات الهيكل التنظيمي' : 'Org Structure Assignments'}
                subtitle={isArabic ? 'سجل تعيينات الموظفين في المناصب التنظيمية' : 'Employee position assignment history'}
                search={search} onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'تعيين جديد' : 'New Assignment'}
                onAddClick={() => navigate('/org-structure-assignments/create')}
                addButtonVisible={perms.create}
                isLoading={isLoading} error={error} items={assignments} meta={meta}
                page={page} perPage={perPage} onPageChange={handlePageChange} onPerPageChange={handlePerPageChange}
                sortBy={sortBy} sortDir={sortDir} onRequestSort={handleRequestSort}
                selectedIds={selectedIds} onSelectAllClick={(e) => handleSelectAllClick(e, assignments)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={perms.delete ? handleBulkDelete : undefined}
                isBulkDeleting={isDeletingAll}
                columns={OrgStructureAssignmentsTableColumns} renderRow={renderRow}
                menuAnchorEl={menuAnchorEl} openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen} onMenuClose={handleMenuClose}
                onMenuEdit={perms.edit ? handleEdit : undefined}
                onMenuDelete={perms.delete ? handleMenuDelete : undefined}
                onMenuCloseAction={selectedItem?.is_active ? handleDeactivate : undefined}
                menuDisableEdit={!perms.edit}
                menuDisableDelete={!perms.delete}
                menuDisableClose={!selectedItem?.is_active}
                isDeletingSingle={isDelete}
                emptyLabelAr="لا توجد تعيينات" emptyLabelEn="No assignments"
                loadErrorAr="حدث خطأ أثناء تحميل التعيينات" loadErrorEn="Failed to load assignments"
            />
        </>
    );
};

export default OrgStructureAssignmentsTable;
